import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { jobApplicationsAPI } from '../../services/api';
import { 
  TrendingUp, 
  Users, 
  Briefcase, 
  Calendar, 
  Target, 
  Clock,
  Award,
  Filter
} from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';

const RecruitmentAnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    department: '',
    jobId: ''
  });
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    loadAnalytics();
    loadDepartments();
  }, []);

  const loadAnalytics = async (customFilters = filters) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (customFilters.startDate) queryParams.append('startDate', customFilters.startDate);
      if (customFilters.endDate) queryParams.append('endDate', customFilters.endDate);
      if (customFilters.department) queryParams.append('department', customFilters.department);
      if (customFilters.jobId) queryParams.append('jobId', customFilters.jobId);

      const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5001/api' : '/api';
      const response = await fetch(`${API_URL}/admin/recruitment/analytics?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      console.log('Analytics API Response:', data);
      console.log('Summary:', data.summary);
      console.log('byStatus:', data.byStatus);
      console.log('byDepartment:', data.byDepartment);
      console.log('byJob:', data.byJob);
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = () => {
    // Get unique departments from existing data or use common ones
    setDepartments([
      'Engineering',
      'Design',
      'Product',
      'Marketing',
      'Sales',
      'Data Science',
      'Infrastructure'
    ]);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const applyFilters = () => {
    loadAnalytics(filters);
  };

  const resetFilters = () => {
    const emptyFilters = {
      startDate: '',
      endDate: '',
      department: '',
      jobId: ''
    };
    setFilters(emptyFilters);
    loadAnalytics(emptyFilters);
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      purple: 'bg-purple-50 text-purple-600',
      orange: 'bg-orange-50 text-orange-600',
      indigo: 'bg-indigo-50 text-indigo-600'
    };

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
        <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        <p className="text-sm font-medium text-gray-600 mt-1">{title}</p>
        {subtitle && (
          <p className="text-xs text-gray-500 mt-2">{subtitle}</p>
        )}
      </div>
    );
  };

  const ProgressBar = ({ label, value, max, color = 'blue' }) => {
    const percentage = max > 0 ? (value / max * 100) : 0;
    const colorClasses = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      red: 'bg-red-500'
    };

    return (
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <span className="text-sm text-gray-600">{value} ({percentage.toFixed(1)}%)</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className={`h-2.5 rounded-full ${colorClasses[color]}`}
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  if (!analytics) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">No analytics data available</p>
        </div>
      </AdminLayout>
    );
  }

  const { 
    summary = {}, 
    byStatus = {}, 
    byDepartment = {}, 
    byJob = [], 
    conversionRates = {}, 
    scoresByStage = {}, 
    timeToHireData = {} 
  } = analytics || {};

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Recruitment Analytics</h1>
            <p className="text-gray-600 mt-1">Track hiring performance and metrics</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select
                value={filters.department}
                onChange={(e) => handleFilterChange('department', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Departments</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={applyFilters}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Apply
              </button>
              <button
                onClick={resetFilters}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <StatCard
            icon={Users}
            title="Total Applications"
            value={summary.totalApplications || 0}
            color="blue"
          />
          <StatCard
            icon={Briefcase}
            title="Active Jobs"
            value={summary.activeJobs || 0}
            color="green"
          />
          <StatCard
            icon={Calendar}
            title="Upcoming Interviews"
            value={summary.upcomingInterviews || 0}
            color="purple"
          />
          <StatCard
            icon={Award}
            title="Hired"
            value={summary.hiredCount || 0}
            color="indigo"
          />
          <StatCard
            icon={Clock}
            title="Avg Time to Hire"
            value={`${summary.avgTimeToHire || 0} days`}
            subtitle={(timeToHireData.samples || 0) > 0 ? `Based on ${timeToHireData.samples} hires` : 'No data'}
            color="orange"
          />
        </div>

        {/* Pipeline by Status - Bar Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Applications by Status</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={[
              { name: 'New', value: byStatus.new || 0, fill: '#3B82F6' },
              { name: 'Screening', value: byStatus.screening || 0, fill: '#8B5CF6' },
              { name: 'Shortlisted', value: byStatus.shortlisted || 0, fill: '#6366F1' },
              { name: 'Interview', value: byStatus['interview-scheduled'] || 0, fill: '#F59E0B' },
              { name: 'Interviewed', value: byStatus.interviewed || 0, fill: '#10B981' },
              { name: 'Offer', value: byStatus.offer || 0, fill: '#3B82F6' },
              { name: 'Hired', value: byStatus.hired || 0, fill: '#059669' },
              { name: 'Rejected', value: byStatus.rejected || 0, fill: '#EF4444' }
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {[
                  { name: 'New', value: byStatus.new || 0, fill: '#3B82F6' },
                  { name: 'Screening', value: byStatus.screening || 0, fill: '#8B5CF6' },
                  { name: 'Shortlisted', value: byStatus.shortlisted || 0, fill: '#6366F1' },
                  { name: 'Interview', value: byStatus['interview-scheduled'] || 0, fill: '#F59E0B' },
                  { name: 'Interviewed', value: byStatus.interviewed || 0, fill: '#10B981' },
                  { name: 'Offer', value: byStatus.offer || 0, fill: '#3B82F6' },
                  { name: 'Hired', value: byStatus.hired || 0, fill: '#059669' },
                  { name: 'Rejected', value: byStatus.rejected || 0, fill: '#EF4444' }
                ].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Conversion Rates - Funnel Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-6">
              <Target className="w-5 h-5 text-gray-600" />
              <h2 className="text-xl font-semibold text-gray-900">Conversion Rates</h2>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart 
                layout="vertical"
                data={[
                  { stage: 'To Screening', rate: parseFloat(conversionRates.screeningRate || 0) },
                  { stage: 'To Shortlist', rate: parseFloat(conversionRates.shortlistedRate || 0) },
                  { stage: 'To Interview', rate: parseFloat(conversionRates.interviewRate || 0) },
                  { stage: 'To Offer', rate: parseFloat(conversionRates.offerRate || 0) },
                  { stage: 'To Hire', rate: parseFloat(conversionRates.hireRate || 0) }
                ]}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis dataKey="stage" type="category" width={100} />
                <Tooltip formatter={(value) => `${value}%`} />
                <Bar dataKey="rate" fill="#3B82F6" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* By Department - Pie Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Applications by Department</h2>
            {Object.keys(byDepartment || {}).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={Object.entries(byDepartment).map(([name, value]) => ({ name, value }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.entries(byDepartment).map((entry, index) => {
                      const colors = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#EC4899'];
                      return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                    })}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                No department data available
              </div>
            )}
          </div>
        </div>

        {/* Top Jobs - Horizontal Bar Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Top Jobs by Applications</h2>
          {(byJob || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart 
                layout="vertical"
                data={(byJob || []).slice(0, 8).map(job => ({
                  name: job.jobTitle.length > 25 ? job.jobTitle.substring(0, 25) + '...' : job.jobTitle,
                  applications: job.count
                }))}
                margin={{ left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={180} />
                <Tooltip />
                <Bar dataKey="applications" fill="#8B5CF6" radius={[0, 8, 8, 0]}>
                  {(byJob || []).slice(0, 8).map((entry, index) => {
                    const colors = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#EC4899', '#14B8A6'];
                    return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[400px] flex items-center justify-center text-gray-500">
              No job application data available
            </div>
          )}
        </div>

        {/* Average Scores by Stage - Line Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Average Scores by Stage</h2>
          {Object.keys(scoresByStage || {}).length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={Object.entries(scoresByStage || {}).map(([stage, data]) => {
                const stageLabels = {
                  'new': 'New',
                  'screening': 'Screening',
                  'shortlisted': 'Shortlisted',
                  'interview-scheduled': 'Interview',
                  'interviewed': 'Interviewed',
                  'offer': 'Offer',
                  'hired': 'Hired'
                };
                return {
                  stage: stageLabels[stage],
                  score: parseFloat(data.avgScore),
                  count: data.count
                };
              })}>
                <defs>
                  <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="stage" />
                <YAxis domain={[0, 5]} />
                <Tooltip content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                        <p className="font-semibold">{payload[0].payload.stage}</p>
                        <p className="text-sm text-blue-600">Score: {payload[0].value}/5.0</p>
                        <p className="text-xs text-gray-500">{payload[0].payload.count} applications</p>
                      </div>
                    );
                  }
                  return null;
                }} />
                <Area 
                  type="monotone" 
                  dataKey="score" 
                  stroke="#3B82F6" 
                  fillOpacity={1} 
                  fill="url(#colorScore)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No scoring data available
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default RecruitmentAnalyticsPage;
